import { useState, useRef, useEffect } from 'react';

const PAPER_WIDTH = 794;
const PAPER_MIN_HEIGHT = 1123;

// ─── TEMPLATE 1: CLASSIC ───
function ClassicTemplate({ resume, photo }) {
  return (
    <div style={{ fontFamily: 'Georgia, serif', color: '#1a1a1a', padding: '32px 36px', background: '#fff', minHeight: '400px' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '20px', marginBottom: '8px' }}>
        {photo && <img src={photo} alt="profile" style={{ width: '80px', height: '80px', borderRadius: '50%', objectFit: 'cover', border: '2px solid #1a1a1a' }} />}
        <div style={{ flex: 1 }}>
          {resume.name && <h1 style={{ fontSize: '26px', fontWeight: '700', margin: '0 0 4px', letterSpacing: '1px' }}>{resume.name}</h1>}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', fontSize: '12px', color: '#555' }}>
            {resume.email && <span>{resume.email}</span>}
            {resume.phone && <span>{resume.phone}</span>}
            {resume.location && <span>{resume.location}</span>}
            {resume.linkedin && <span>{resume.linkedin}</span>}
          </div>
        </div>
      </div>
      <hr style={{ border: 'none', borderTop: '2px solid #1a1a1a', margin: '12px 0' }} />
      {resume.summary && (<><div style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1.5px', margin: '12px 0 6px' }}>Summary</div><p style={{ fontSize: '13px', lineHeight: '1.6', margin: 0 }}>{resume.summary}</p><hr style={{ border: 'none', borderTop: '1px solid #ccc', margin: '10px 0' }} /></>)}
      {resume.experience?.length > 0 && (<><div style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1.5px', margin: '12px 0 6px' }}>Experience</div>{resume.experience.map((exp, i) => (<div key={i} style={{ marginBottom: '10px' }}><div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ fontWeight: '700', fontSize: '13px' }}>{exp.title}{exp.company && <span style={{ fontWeight: '400', color: '#666', fontStyle: 'italic' }}> @ {exp.company}</span>}</span><span style={{ fontSize: '12px', color: '#888' }}>{exp.duration}</span></div>{exp.bullets?.length > 0 && <ul style={{ paddingLeft: '18px', margin: '4px 0' }}>{exp.bullets.map((b, j) => <li key={j} style={{ fontSize: '12px', lineHeight: '1.6', marginBottom: '2px' }}>{b}</li>)}</ul>}</div>))}<hr style={{ border: 'none', borderTop: '1px solid #ccc', margin: '10px 0' }} /></>)}
      {resume.education?.length > 0 && (<><div style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1.5px', margin: '12px 0 6px' }}>Education</div>{resume.education.map((edu, i) => (<div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '4px' }}><span><strong>{edu.degree}</strong>{edu.school && ` — ${edu.school}`}</span><span style={{ color: '#888', fontSize: '12px' }}>{edu.year}</span></div>))}<hr style={{ border: 'none', borderTop: '1px solid #ccc', margin: '10px 0' }} /></>)}
      {resume.skills?.length > 0 && (<><div style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1.5px', margin: '12px 0 6px' }}>Skills</div><div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>{resume.skills.map((s, i) => <span key={i} style={{ background: '#f0f0f0', padding: '3px 10px', borderRadius: '12px', fontSize: '12px' }}>{s}</span>)}</div></>)}
    </div>
  );
}

// ─── TEMPLATE 2: MODERN (Purple Sidebar) ───
function ModernTemplate({ resume, photo }) {
  return (
    <div style={{ display: 'flex', fontFamily: "'Inter', sans-serif", minHeight: '400px', background: '#fff' }}>
      <div style={{ width: '35%', background: '#7C3AED', color: '#fff', padding: '28px 20px' }}>
        {photo && <img src={photo} alt="profile" style={{ width: '80px', height: '80px', borderRadius: '50%', objectFit: 'cover', border: '3px solid rgba(255,255,255,0.5)', marginBottom: '12px', display: 'block' }} />}
        {resume.name && <h1 style={{ fontSize: '18px', fontWeight: '700', margin: '0 0 4px' }}>{resume.name}</h1>}
        <div style={{ fontSize: '11px', opacity: 0.8, marginBottom: '20px', lineHeight: '1.6' }}>
          {resume.email && <div>{resume.email}</div>}
          {resume.phone && <div>{resume.phone}</div>}
          {resume.location && <div>{resume.location}</div>}
          {resume.linkedin && <div>{resume.linkedin}</div>}
        </div>
        {resume.skills?.length > 0 && (<><div style={{ fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '8px', opacity: 0.7 }}>Skills</div>{resume.skills.map((s, i) => <div key={i} style={{ background: 'rgba(255,255,255,0.15)', padding: '4px 8px', borderRadius: '4px', fontSize: '11px', marginBottom: '4px' }}>{s}</div>)}</>)}
      </div>
      <div style={{ flex: 1, padding: '28px 24px' }}>
        {resume.summary && (<><div style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1.5px', color: '#7C3AED', marginBottom: '6px' }}>About Me</div><p style={{ fontSize: '12px', lineHeight: '1.6', color: '#444', marginBottom: '16px' }}>{resume.summary}</p></>)}
        {resume.experience?.length > 0 && (<><div style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1.5px', color: '#7C3AED', marginBottom: '8px' }}>Experience</div>{resume.experience.map((exp, i) => (<div key={i} style={{ marginBottom: '12px', paddingLeft: '10px', borderLeft: '2px solid #7C3AED' }}><div style={{ fontWeight: '700', fontSize: '13px' }}>{exp.title}</div><div style={{ fontSize: '11px', color: '#7C3AED', marginBottom: '4px' }}>{exp.company} · {exp.duration}</div>{exp.bullets?.map((b, j) => <div key={j} style={{ fontSize: '11px', color: '#555', lineHeight: '1.5', marginBottom: '2px' }}>• {b}</div>)}</div>))}</>)}
        {resume.education?.length > 0 && (<><div style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1.5px', color: '#7C3AED', margin: '12px 0 8px' }}>Education</div>{resume.education.map((edu, i) => (<div key={i} style={{ marginBottom: '6px' }}><div style={{ fontWeight: '700', fontSize: '12px' }}>{edu.degree}</div><div style={{ fontSize: '11px', color: '#666' }}>{edu.school} · {edu.year}</div></div>))}</>)}
      </div>
    </div>
  );
}

// ─── TEMPLATE 3: MINIMAL ───
function MinimalTemplate({ resume, photo }) {
  return (
    <div style={{ fontFamily: "'Inter', sans-serif", color: '#2d2d2d', padding: '36px 40px', background: '#fff', minHeight: '400px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
        {photo && <img src={photo} alt="profile" style={{ width: '70px', height: '70px', borderRadius: '8px', objectFit: 'cover' }} />}
        <div>
          {resume.name && <h1 style={{ fontSize: '28px', fontWeight: '300', margin: '0 0 4px', letterSpacing: '2px', textTransform: 'uppercase' }}>{resume.name}</h1>}
          <div style={{ display: 'flex', gap: '16px', fontSize: '11px', color: '#999' }}>
            {resume.email && <span>{resume.email}</span>}
            {resume.phone && <span>{resume.phone}</span>}
            {resume.location && <span>{resume.location}</span>}
          </div>
        </div>
      </div>
      {resume.summary && (<><div style={{ height: '1px', background: '#e0e0e0', margin: '0 0 16px' }} /><p style={{ fontSize: '12px', lineHeight: '1.8', color: '#666', margin: '0 0 16px' }}>{resume.summary}</p></>)}
      {resume.experience?.length > 0 && (<><div style={{ height: '1px', background: '#e0e0e0', margin: '0 0 14px' }} /><div style={{ fontSize: '10px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '2px', color: '#999', marginBottom: '12px' }}>Experience</div>{resume.experience.map((exp, i) => (<div key={i} style={{ display: 'flex', gap: '16px', marginBottom: '14px' }}><div style={{ fontSize: '11px', color: '#999', width: '80px', flexShrink: 0, paddingTop: '2px' }}>{exp.duration}</div><div style={{ flex: 1 }}><div style={{ fontWeight: '600', fontSize: '13px', marginBottom: '2px' }}>{exp.title}</div><div style={{ fontSize: '11px', color: '#0F766E', marginBottom: '6px' }}>{exp.company}</div>{exp.bullets?.map((b, j) => <div key={j} style={{ fontSize: '11px', color: '#666', lineHeight: '1.6', marginBottom: '2px' }}>— {b}</div>)}</div></div>))}</>)}
      {resume.education?.length > 0 && (<><div style={{ height: '1px', background: '#e0e0e0', margin: '0 0 14px' }} /><div style={{ fontSize: '10px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '2px', color: '#999', marginBottom: '12px' }}>Education</div>{resume.education.map((edu, i) => (<div key={i} style={{ display: 'flex', gap: '16px', marginBottom: '8px' }}><div style={{ fontSize: '11px', color: '#999', width: '80px', flexShrink: 0 }}>{edu.year}</div><div><div style={{ fontWeight: '600', fontSize: '12px' }}>{edu.degree}</div><div style={{ fontSize: '11px', color: '#666' }}>{edu.school}</div></div></div>))}</>)}
      {resume.skills?.length > 0 && (<><div style={{ height: '1px', background: '#e0e0e0', margin: '14px 0' }} /><div style={{ fontSize: '10px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '2px', color: '#999', marginBottom: '10px' }}>Skills</div><div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>{resume.skills.map((s, i) => <span key={i} style={{ fontSize: '11px', color: '#555', border: '1px solid #ddd', padding: '3px 10px', borderRadius: '2px' }}>{s}</span>)}</div></>)}
    </div>
  );
}

// ─── TEMPLATE 4: BOLD ───
function BoldTemplate({ resume, photo }) {
  return (
    <div style={{ fontFamily: "'Inter', sans-serif", background: '#fff', minHeight: '400px' }}>
      <div style={{ background: '#1E3A5F', color: '#fff', padding: '28px 32px', display: 'flex', alignItems: 'center', gap: '20px' }}>
        {photo && <img src={photo} alt="profile" style={{ width: '85px', height: '85px', borderRadius: '50%', objectFit: 'cover', border: '3px solid #F59E0B' }} />}
        <div style={{ flex: 1 }}>
          {resume.name && <h1 style={{ fontSize: '26px', fontWeight: '800', margin: '0 0 6px', textTransform: 'uppercase', letterSpacing: '2px' }}>{resume.name}</h1>}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '14px', fontSize: '11px', opacity: 0.8 }}>
            {resume.email && <span>✉ {resume.email}</span>}
            {resume.phone && <span>📞 {resume.phone}</span>}
            {resume.location && <span>📍 {resume.location}</span>}
          </div>
        </div>
      </div>
      <div style={{ padding: '24px 32px' }}>
        {resume.summary && (<><div style={{ fontSize: '13px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '2px', color: '#1E3A5F', borderBottom: '3px solid #F59E0B', paddingBottom: '4px', marginBottom: '10px' }}>Profile</div><p style={{ fontSize: '12px', lineHeight: '1.7', color: '#444', marginBottom: '16px' }}>{resume.summary}</p></>)}
        {resume.experience?.length > 0 && (<><div style={{ fontSize: '13px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '2px', color: '#1E3A5F', borderBottom: '3px solid #F59E0B', paddingBottom: '4px', marginBottom: '12px' }}>Experience</div>{resume.experience.map((exp, i) => (<div key={i} style={{ marginBottom: '14px' }}><div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}><span style={{ fontWeight: '700', fontSize: '14px', color: '#1E3A5F' }}>{exp.title}</span><span style={{ fontSize: '11px', color: '#888', background: '#F3F4F6', padding: '2px 8px', borderRadius: '4px' }}>{exp.duration}</span></div><div style={{ fontSize: '12px', color: '#F59E0B', fontWeight: '600', marginBottom: '6px' }}>{exp.company}</div>{exp.bullets?.map((b, j) => <div key={j} style={{ fontSize: '12px', color: '#555', lineHeight: '1.6', paddingLeft: '12px', borderLeft: '2px solid #F59E0B', marginBottom: '3px' }}>{b}</div>)}</div>))}</>)}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginTop: '8px' }}>
          {resume.education?.length > 0 && (<div><div style={{ fontSize: '13px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '2px', color: '#1E3A5F', borderBottom: '3px solid #F59E0B', paddingBottom: '4px', marginBottom: '10px' }}>Education</div>{resume.education.map((edu, i) => (<div key={i} style={{ marginBottom: '8px' }}><div style={{ fontWeight: '700', fontSize: '12px' }}>{edu.degree}</div><div style={{ fontSize: '11px', color: '#666' }}>{edu.school}</div><div style={{ fontSize: '11px', color: '#999' }}>{edu.year}</div></div>))}</div>)}
          {resume.skills?.length > 0 && (<div><div style={{ fontSize: '13px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '2px', color: '#1E3A5F', borderBottom: '3px solid #F59E0B', paddingBottom: '4px', marginBottom: '10px' }}>Skills</div><div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>{resume.skills.map((s, i) => <span key={i} style={{ background: '#1E3A5F', color: '#fff', padding: '3px 8px', borderRadius: '4px', fontSize: '11px' }}>{s}</span>)}</div></div>)}
        </div>
      </div>
    </div>
  );
}

// ─── TEMPLATE 5: PROFESSIONAL ───
function ProfessionalTemplate({ resume, photo }) {
  return (
    <div style={{ fontFamily: "'Inter', sans-serif", background: '#fff', minHeight: '400px' }}>
      <div style={{ background: '#B91C1C', padding: '24px 32px', display: 'flex', alignItems: 'center', gap: '18px' }}>
        {photo && <img src={photo} alt="profile" style={{ width: '75px', height: '75px', borderRadius: '4px', objectFit: 'cover', border: '2px solid rgba(255,255,255,0.5)' }} />}
        <div style={{ flex: 1 }}>
          {resume.name && <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#fff', margin: '0 0 6px' }}>{resume.name}</h1>}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', fontSize: '11px', color: 'rgba(255,255,255,0.8)' }}>
            {resume.email && <span>{resume.email}</span>}
            {resume.phone && <span>{resume.phone}</span>}
            {resume.location && <span>{resume.location}</span>}
            {resume.linkedin && <span>{resume.linkedin}</span>}
          </div>
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '0', minHeight: '300px' }}>
        <div style={{ padding: '24px 28px', borderRight: '1px solid #f0f0f0' }}>
          {resume.summary && (<><div style={{ fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1.5px', color: '#B91C1C', marginBottom: '8px' }}>Professional Summary</div><p style={{ fontSize: '12px', lineHeight: '1.7', color: '#444', marginBottom: '16px' }}>{resume.summary}</p></>)}
          {resume.experience?.length > 0 && (<><div style={{ fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1.5px', color: '#B91C1C', marginBottom: '10px' }}>Work Experience</div>{resume.experience.map((exp, i) => (<div key={i} style={{ marginBottom: '14px', paddingBottom: '14px', borderBottom: '1px solid #f5f5f5' }}><div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ fontWeight: '700', fontSize: '13px' }}>{exp.title}</span><span style={{ fontSize: '11px', color: '#B91C1C', fontWeight: '600' }}>{exp.duration}</span></div><div style={{ fontSize: '11px', color: '#666', marginBottom: '6px' }}>{exp.company}</div>{exp.bullets?.map((b, j) => <div key={j} style={{ fontSize: '11px', color: '#555', lineHeight: '1.6', paddingLeft: '10px', position: 'relative', marginBottom: '2px' }}>• {b}</div>)}</div>))}</>)}
        </div>
        <div style={{ padding: '24px 20px', background: '#FEF2F2' }}>
          {resume.skills?.length > 0 && (<><div style={{ fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1.5px', color: '#B91C1C', marginBottom: '10px' }}>Skills</div>{resume.skills.map((s, i) => (<div key={i} style={{ fontSize: '11px', color: '#333', padding: '5px 0', borderBottom: '1px solid #fecaca' }}>{s}</div>))}</>)}
          {resume.education?.length > 0 && (<><div style={{ fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1.5px', color: '#B91C1C', margin: '16px 0 10px' }}>Education</div>{resume.education.map((edu, i) => (<div key={i} style={{ marginBottom: '10px' }}><div style={{ fontWeight: '700', fontSize: '12px' }}>{edu.degree}</div><div style={{ fontSize: '11px', color: '#666' }}>{edu.school}</div><div style={{ fontSize: '11px', color: '#B91C1C' }}>{edu.year}</div></div>))}</>)}
        </div>
      </div>
    </div>
  );
}


// ─── TEMPLATE 6: EXECUTIVE ───
function ExecutiveTemplate({ resume, photo }) {
  return (
    <div style={{ fontFamily: "'Inter', sans-serif", background: '#fff', minHeight: '1123px', width: '794px', boxSizing: 'border-box' }}>
      <div style={{ background: '#064E3B', color: '#fff', padding: '40px 48px', display: 'flex', alignItems: 'center', gap: '24px' }}>
        {photo && <img src={photo} alt="profile" style={{ width: '100px', height: '100px', borderRadius: '8px', objectFit: 'cover', border: '3px solid rgba(255,255,255,0.3)', flexShrink: 0 }} />}
        <div style={{ flex: 1 }}>
          {resume.name && <h1 style={{ fontSize: '30px', fontWeight: '700', margin: '0 0 8px', letterSpacing: '1px' }}>{resume.name}</h1>}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', fontSize: '12px', opacity: 0.85 }}>
            {resume.email && <span>✉ {resume.email}</span>}
            {resume.phone && <span>📞 {resume.phone}</span>}
            {resume.location && <span>📍 {resume.location}</span>}
            {resume.linkedin && <span>🔗 {resume.linkedin}</span>}
          </div>
        </div>
      </div>
      <div style={{ display: 'flex' }}>
        <div style={{ flex: 2, padding: '32px 36px', borderRight: '1px solid #e5e7eb' }}>
          {resume.summary && <><div style={{ fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '2px', color: '#064E3B', marginBottom: '10px' }}>Executive Summary</div><p style={{ fontSize: '13px', lineHeight: '1.8', color: '#444', marginBottom: '24px' }}>{resume.summary}</p></>}
          {resume.experience?.length > 0 && <><div style={{ fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '2px', color: '#064E3B', marginBottom: '14px' }}>Professional Experience</div>{resume.experience.map((exp, i) => (<div key={i} style={{ marginBottom: '18px', paddingLeft: '14px', borderLeft: '3px solid #10b981' }}><div style={{ fontWeight: '700', fontSize: '15px' }}>{exp.title}</div><div style={{ fontSize: '12px', color: '#064E3B', fontWeight: '600', marginBottom: '6px' }}>{exp.company} · {exp.duration}</div>{exp.bullets?.map((b, j) => <div key={j} style={{ fontSize: '13px', color: '#555', lineHeight: '1.7', marginBottom: '3px' }}>• {b}</div>)}</div>))}</>}
        </div>
        <div style={{ flex: 1, padding: '32px 24px', background: '#F0FDF4' }}>
          {resume.skills?.length > 0 && <><div style={{ fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '2px', color: '#064E3B', marginBottom: '12px' }}>Core Skills</div>{resume.skills.map((s, i) => (<div key={i} style={{ fontSize: '13px', color: '#333', padding: '6px 0', borderBottom: '1px solid #bbf7d0' }}>{s}</div>))}</>}
          {resume.education?.length > 0 && <><div style={{ fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '2px', color: '#064E3B', margin: '20px 0 12px' }}>Education</div>{resume.education.map((edu, i) => (<div key={i} style={{ marginBottom: '12px' }}><div style={{ fontWeight: '700', fontSize: '13px' }}>{edu.degree}</div><div style={{ fontSize: '12px', color: '#666' }}>{edu.school}</div><div style={{ fontSize: '12px', color: '#064E3B' }}>{edu.year}</div></div>))}</>}
        </div>
      </div>
    </div>
  );
}

// ─── TEMPLATE 7: CREATIVE ───
function CreativeTemplate({ resume, photo }) {
  return (
    <div style={{ fontFamily: "'Inter', sans-serif", background: '#fff', minHeight: '1123px', width: '794px', boxSizing: 'border-box' }}>
      <div style={{ background: 'linear-gradient(135deg, #9D174D, #DB2777)', color: '#fff', padding: '48px 48px 32px', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: '-40px', right: '-40px', width: '200px', height: '200px', borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: '24px', position: 'relative' }}>
          {photo && <img src={photo} alt="profile" style={{ width: '100px', height: '100px', borderRadius: '50%', objectFit: 'cover', border: '4px solid rgba(255,255,255,0.4)', flexShrink: 0 }} />}
          <div>
            {resume.name && <h1 style={{ fontSize: '32px', fontWeight: '800', margin: '0 0 8px' }}>{resume.name}</h1>}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '14px', fontSize: '12px', opacity: 0.9 }}>
              {resume.email && <span>✉ {resume.email}</span>}
              {resume.phone && <span>📞 {resume.phone}</span>}
              {resume.location && <span>📍 {resume.location}</span>}
            </div>
          </div>
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
        <div style={{ padding: '32px 36px', borderRight: '1px solid #fce7f3' }}>
          {resume.summary && <><div style={{ fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '2px', color: '#9D174D', marginBottom: '10px' }}>About Me</div><p style={{ fontSize: '13px', lineHeight: '1.8', color: '#444', marginBottom: '24px' }}>{resume.summary}</p></>}
          {resume.experience?.length > 0 && <><div style={{ fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '2px', color: '#9D174D', marginBottom: '14px' }}>Experience</div>{resume.experience.map((exp, i) => (<div key={i} style={{ marginBottom: '18px' }}><div style={{ fontWeight: '700', fontSize: '14px', color: '#1a1a1a' }}>{exp.title}</div><div style={{ fontSize: '12px', color: '#9D174D', marginBottom: '6px', fontWeight: '600' }}>{exp.company} · {exp.duration}</div>{exp.bullets?.map((b, j) => <div key={j} style={{ fontSize: '12px', color: '#555', lineHeight: '1.7', paddingLeft: '10px', borderLeft: '2px solid #fbcfe8', marginBottom: '3px' }}>{b}</div>)}</div>))}</>}
        </div>
        <div style={{ padding: '32px 32px', background: '#FDF2F8' }}>
          {resume.skills?.length > 0 && <><div style={{ fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '2px', color: '#9D174D', marginBottom: '12px' }}>Skills</div><div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '24px' }}>{resume.skills.map((s, i) => <span key={i} style={{ background: '#9D174D', color: '#fff', padding: '4px 12px', borderRadius: '20px', fontSize: '12px' }}>{s}</span>)}</div></>}
          {resume.education?.length > 0 && <><div style={{ fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '2px', color: '#9D174D', marginBottom: '12px' }}>Education</div>{resume.education.map((edu, i) => (<div key={i} style={{ marginBottom: '14px', padding: '12px', background: '#fff', borderRadius: '8px', border: '1px solid #fbcfe8' }}><div style={{ fontWeight: '700', fontSize: '13px' }}>{edu.degree}</div><div style={{ fontSize: '12px', color: '#666' }}>{edu.school}</div><div style={{ fontSize: '12px', color: '#9D174D' }}>{edu.year}</div></div>))}</>}
        </div>
      </div>
    </div>
  );
}

// ─── TEMPLATE 8: TECH ───
function TechTemplate({ resume, photo }) {
  return (
    <div style={{ fontFamily: "'Courier New', monospace", background: '#0f172a', minHeight: '1123px', width: '794px', color: '#e2e8f0', boxSizing: 'border-box' }}>
      <div style={{ padding: '40px 48px', borderBottom: '1px solid #1e293b' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
          {photo && <img src={photo} alt="profile" style={{ width: '90px', height: '90px', borderRadius: '8px', objectFit: 'cover', border: '2px solid #38bdf8', flexShrink: 0 }} />}
          <div style={{ flex: 1 }}>
            <div style={{ color: '#38bdf8', fontSize: '13px', marginBottom: '4px' }}>{'// developer profile'}</div>
            {resume.name && <h1 style={{ fontSize: '30px', fontWeight: '700', margin: '0 0 8px', color: '#f1f5f9' }}>{resume.name}</h1>}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', fontSize: '12px', color: '#94a3b8' }}>
              {resume.email && <span>✉ {resume.email}</span>}
              {resume.phone && <span>📞 {resume.phone}</span>}
              {resume.linkedin && <span>🔗 {resume.linkedin}</span>}
            </div>
          </div>
        </div>
      </div>
      <div style={{ display: 'flex' }}>
        <div style={{ flex: 2, padding: '32px 36px' }}>
          {resume.summary && <><div style={{ color: '#38bdf8', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '10px' }}>{'/* about */'}</div><p style={{ fontSize: '13px', lineHeight: '1.8', color: '#94a3b8', marginBottom: '24px', fontFamily: "'Inter', sans-serif" }}>{resume.summary}</p></>}
          {resume.experience?.length > 0 && <><div style={{ color: '#38bdf8', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '14px' }}>{'/* experience */'}</div>{resume.experience.map((exp, i) => (<div key={i} style={{ marginBottom: '18px', padding: '14px', background: '#1e293b', borderRadius: '6px', borderLeft: '3px solid #38bdf8' }}><div style={{ fontWeight: '700', fontSize: '14px', color: '#f1f5f9', marginBottom: '4px' }}>{exp.title}</div><div style={{ fontSize: '12px', color: '#38bdf8', marginBottom: '8px' }}>{exp.company} · {exp.duration}</div>{exp.bullets?.map((b, j) => <div key={j} style={{ fontSize: '12px', color: '#94a3b8', lineHeight: '1.7', marginBottom: '3px', fontFamily: "'Inter', sans-serif" }}>→ {b}</div>)}</div>))}</>}
        </div>
        <div style={{ flex: 1, padding: '32px 24px', borderLeft: '1px solid #1e293b' }}>
          {resume.skills?.length > 0 && <><div style={{ color: '#38bdf8', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '12px' }}>{'/* skills */'}</div>{resume.skills.map((s, i) => (<div key={i} style={{ fontSize: '12px', color: '#e2e8f0', padding: '6px 10px', background: '#1e293b', borderRadius: '4px', marginBottom: '6px', fontFamily: "'Inter', sans-serif" }}>▸ {s}</div>))}</>}
          {resume.education?.length > 0 && <><div style={{ color: '#38bdf8', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '2px', margin: '20px 0 12px' }}>{'/* education */'}</div>{resume.education.map((edu, i) => (<div key={i} style={{ marginBottom: '12px', padding: '10px', background: '#1e293b', borderRadius: '4px' }}><div style={{ fontWeight: '700', fontSize: '12px', color: '#f1f5f9', fontFamily: "'Inter', sans-serif" }}>{edu.degree}</div><div style={{ fontSize: '11px', color: '#94a3b8', fontFamily: "'Inter', sans-serif" }}>{edu.school}</div><div style={{ fontSize: '11px', color: '#38bdf8', fontFamily: "'Inter', sans-serif" }}>{edu.year}</div></div>))}</>}
        </div>
      </div>
    </div>
  );
}

// ─── TEMPLATE 9: ELEGANT ───
function ElegantTemplate({ resume, photo }) {
  return (
    <div style={{ fontFamily: 'Georgia, serif', background: '#FFFBEB', minHeight: '1123px', width: '794px', padding: '60px 64px', boxSizing: 'border-box' }}>
      <div style={{ textAlign: 'center', borderBottom: '2px solid #78350F', paddingBottom: '24px', marginBottom: '28px' }}>
        {photo && <img src={photo} alt="profile" style={{ width: '100px', height: '100px', borderRadius: '50%', objectFit: 'cover', border: '3px solid #d97706', marginBottom: '16px', display: 'block', margin: '0 auto 16px' }} />}
        {resume.name && <h1 style={{ fontSize: '34px', fontWeight: '700', color: '#78350F', margin: '0 0 8px', letterSpacing: '2px' }}>{resume.name}</h1>}
        <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: '16px', fontSize: '12px', color: '#92400e' }}>
          {resume.email && <span>✉ {resume.email}</span>}
          {resume.phone && <span>📞 {resume.phone}</span>}
          {resume.location && <span>📍 {resume.location}</span>}
        </div>
      </div>
      {resume.summary && <><div style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '3px', color: '#78350F', textAlign: 'center', marginBottom: '10px' }}>Profile</div><p style={{ fontSize: '13px', lineHeight: '1.9', color: '#555', marginBottom: '24px', textAlign: 'center', fontStyle: 'italic' }}>{resume.summary}</p><div style={{ height: '1px', background: '#d97706', marginBottom: '24px' }} /></>}
      {resume.experience?.length > 0 && <><div style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '3px', color: '#78350F', marginBottom: '16px' }}>Experience</div>{resume.experience.map((exp, i) => (<div key={i} style={{ marginBottom: '18px' }}><div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}><span style={{ fontWeight: '700', fontSize: '15px', color: '#78350F' }}>{exp.title}</span><span style={{ fontSize: '12px', color: '#92400e' }}>{exp.duration}</span></div><div style={{ fontSize: '13px', color: '#d97706', fontStyle: 'italic', marginBottom: '6px' }}>{exp.company}</div>{exp.bullets?.map((b, j) => <div key={j} style={{ fontSize: '13px', color: '#555', lineHeight: '1.7', paddingLeft: '14px', borderLeft: '2px solid #d97706', marginBottom: '3px' }}>{b}</div>)}</div>))}<div style={{ height: '1px', background: '#d97706', margin: '16px 0' }} /></>}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '32px' }}>
        {resume.education?.length > 0 && <div><div style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '3px', color: '#78350F', marginBottom: '14px' }}>Education</div>{resume.education.map((edu, i) => (<div key={i} style={{ marginBottom: '12px' }}><div style={{ fontWeight: '700', fontSize: '14px', color: '#78350F' }}>{edu.degree}</div><div style={{ fontSize: '13px', color: '#666', fontStyle: 'italic' }}>{edu.school}</div><div style={{ fontSize: '12px', color: '#92400e' }}>{edu.year}</div></div>))}</div>}
        {resume.skills?.length > 0 && <div><div style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '3px', color: '#78350F', marginBottom: '14px' }}>Skills</div><div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>{resume.skills.map((s, i) => <span key={i} style={{ background: '#78350F', color: '#FFFBEB', padding: '4px 12px', borderRadius: '20px', fontSize: '12px' }}>{s}</span>)}</div></div>}
      </div>
    </div>
  );
}

// ─── TEMPLATE 10: FRESH ───
function FreshTemplate({ resume, photo }) {
  return (
    <div style={{ fontFamily: "'Inter', sans-serif", background: '#fff', minHeight: '1123px', width: '794px', boxSizing: 'border-box' }}>
      <div style={{ background: '#1D4ED8', padding: '36px 48px', display: 'flex', alignItems: 'center', gap: '24px' }}>
        {photo && <img src={photo} alt="profile" style={{ width: '95px', height: '95px', borderRadius: '12px', objectFit: 'cover', border: '3px solid rgba(255,255,255,0.4)', flexShrink: 0 }} />}
        <div style={{ flex: 1 }}>
          {resume.name && <h1 style={{ fontSize: '28px', fontWeight: '700', color: '#fff', margin: '0 0 8px' }}>{resume.name}</h1>}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', fontSize: '12px', color: 'rgba(255,255,255,0.85)' }}>
            {resume.email && <span>✉ {resume.email}</span>}
            {resume.phone && <span>📞 {resume.phone}</span>}
            {resume.location && <span>📍 {resume.location}</span>}
            {resume.linkedin && <span>🔗 {resume.linkedin}</span>}
          </div>
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr' }}>
        <div style={{ padding: '32px 36px' }}>
          {resume.summary && <><div style={{ fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '2px', color: '#1D4ED8', marginBottom: '10px' }}>About</div><p style={{ fontSize: '13px', lineHeight: '1.8', color: '#444', marginBottom: '24px' }}>{resume.summary}</p></>}
          {resume.experience?.length > 0 && <><div style={{ fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '2px', color: '#1D4ED8', marginBottom: '14px' }}>Experience</div>{resume.experience.map((exp, i) => (<div key={i} style={{ marginBottom: '18px', paddingBottom: '18px', borderBottom: '1px solid #eff6ff' }}><div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3px' }}><span style={{ fontWeight: '700', fontSize: '15px' }}>{exp.title}</span><span style={{ fontSize: '12px', color: '#1D4ED8', background: '#eff6ff', padding: '2px 10px', borderRadius: '20px', fontWeight: '600' }}>{exp.duration}</span></div><div style={{ fontSize: '13px', color: '#60a5fa', marginBottom: '8px', fontWeight: '600' }}>{exp.company}</div>{exp.bullets?.map((b, j) => <div key={j} style={{ fontSize: '13px', color: '#555', lineHeight: '1.7', marginBottom: '3px' }}>• {b}</div>)}</div>))}</>}
        </div>
        <div style={{ padding: '32px 24px', background: '#EFF6FF', borderLeft: '1px solid #dbeafe' }}>
          {resume.skills?.length > 0 && <><div style={{ fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '2px', color: '#1D4ED8', marginBottom: '12px' }}>Skills</div>{resume.skills.map((s, i) => (<div key={i} style={{ fontSize: '13px', color: '#1e40af', padding: '6px 10px', background: '#fff', borderRadius: '6px', marginBottom: '6px', border: '1px solid #bfdbfe' }}>{s}</div>))}</>}
          {resume.education?.length > 0 && <><div style={{ fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '2px', color: '#1D4ED8', margin: '20px 0 12px' }}>Education</div>{resume.education.map((edu, i) => (<div key={i} style={{ marginBottom: '14px', padding: '12px', background: '#fff', borderRadius: '8px', border: '1px solid #bfdbfe' }}><div style={{ fontWeight: '700', fontSize: '13px' }}>{edu.degree}</div><div style={{ fontSize: '12px', color: '#666' }}>{edu.school}</div><div style={{ fontSize: '12px', color: '#1D4ED8' }}>{edu.year}</div></div>))}</>}
        </div>
      </div>
    </div>
  );
}

// ─── TEMPLATE 11: BLUE SIDEBAR (Kristi Laar Style) ───
function Template11({ resume, photo }) {
  return (
    <div style={{ display: 'flex', fontFamily: 'Georgia, serif', minHeight: '1123px', width: '794px', background: '#fff', boxSizing: 'border-box' }}>
      {/* Left Sidebar - Light Blue */}
      <div style={{ width: '260px', background: '#dce6f0', flexShrink: 0, padding: '0', position: 'relative' }}>
        {/* Photo area with frame effect */}
        <div style={{ position: 'relative', padding: '40px 24px 24px', marginBottom: '16px' }}>
          <div style={{ border: '2px solid #a0b4c8', width: '80px', height: '80px', position: 'absolute', top: '28px', left: '16px' }} />
          {photo
            ? <img src={photo} alt="profile" style={{ width: '120px', height: '140px', objectFit: 'cover', position: 'relative', zIndex: 1, marginLeft: '20px', marginTop: '12px', display: 'block' }} />
            : <div style={{ width: '120px', height: '140px', background: '#b0c4d8', marginLeft: '20px', marginTop: '12px' }} />
          }
        </div>

        {/* Contact */}
        <div style={{ padding: '16px 24px', borderTop: '1px solid #a0b4c8', marginBottom: '8px' }}>
          <div style={{ fontSize: '11px', fontWeight: '700', letterSpacing: '2px', textTransform: 'uppercase', color: '#1a2a3a', marginBottom: '10px', fontFamily: "'Inter', sans-serif" }}>Contact</div>
          {resume.location && <div style={{ fontSize: '11px', color: '#333', lineHeight: '1.6', marginBottom: '4px' }}>{resume.location}</div>}
          {resume.phone && <div style={{ fontSize: '11px', color: '#333', lineHeight: '1.6', marginBottom: '4px' }}>{resume.phone}</div>}
          {resume.email && <div style={{ fontSize: '11px', color: '#333', lineHeight: '1.6', marginBottom: '4px' }}>{resume.email}</div>}
          {resume.linkedin && <div style={{ fontSize: '11px', color: '#333', lineHeight: '1.6' }}>{resume.linkedin}</div>}
        </div>

        {/* Skills as Communication/Leadership style */}
        {resume.skills?.length > 0 && (
          <div style={{ padding: '16px 24px', borderTop: '1px solid #a0b4c8', marginBottom: '8px' }}>
            <div style={{ fontSize: '11px', fontWeight: '700', letterSpacing: '2px', textTransform: 'uppercase', color: '#1a2a3a', marginBottom: '10px', fontFamily: "'Inter', sans-serif" }}>Skills</div>
            {resume.skills.map((s, i) => <div key={i} style={{ fontSize: '11px', color: '#444', lineHeight: '1.7' }}>• {s}</div>)}
          </div>
        )}

        {/* Summary in sidebar */}
        {resume.summary && (
          <div style={{ padding: '16px 24px', borderTop: '1px solid #a0b4c8' }}>
            <div style={{ fontSize: '11px', fontWeight: '700', letterSpacing: '2px', textTransform: 'uppercase', color: '#1a2a3a', marginBottom: '10px', fontFamily: "'Inter', sans-serif" }}>Profile</div>
            <p style={{ fontSize: '11px', color: '#444', lineHeight: '1.7', margin: 0 }}>{resume.summary}</p>
          </div>
        )}
      </div>

      {/* Right Content */}
      <div style={{ flex: 1, padding: '48px 40px', boxSizing: 'border-box' }}>
        {/* Name */}
        <div style={{ marginBottom: '8px' }}>
          {resume.name && (
            <>
              <div style={{ fontSize: '36px', fontWeight: '700', letterSpacing: '6px', textTransform: 'uppercase', color: '#1a2a3a', lineHeight: '1.1' }}>
                {resume.name.split(' ')[0]}
              </div>
              <div style={{ fontSize: '36px', fontWeight: '400', letterSpacing: '6px', textTransform: 'uppercase', color: '#4a6a8a', lineHeight: '1.1' }}>
                {resume.name.split(' ').slice(1).join(' ')}
              </div>
            </>
          )}
          <div style={{ height: '2px', background: '#4a6a8a', margin: '12px 0' }} />
        </div>

        {/* Education */}
        {resume.education?.length > 0 && (
          <div style={{ marginBottom: '24px' }}>
            <div style={{ fontSize: '13px', fontWeight: '700', letterSpacing: '2px', textTransform: 'uppercase', color: '#1a2a3a', marginBottom: '12px', fontFamily: "'Inter', sans-serif" }}>Education</div>
            {resume.education.map((edu, i) => (
              <div key={i} style={{ marginBottom: '10px' }}>
                <div style={{ fontSize: '13px', color: '#333' }}>{edu.school}</div>
                <div style={{ fontSize: '13px', fontWeight: '600', color: '#1a2a3a' }}>{edu.degree}</div>
                <div style={{ fontSize: '12px', color: '#666' }}>{edu.year}</div>
              </div>
            ))}
            <div style={{ height: '1px', background: '#ccc', marginTop: '16px' }} />
          </div>
        )}

        {/* Experience */}
        {resume.experience?.length > 0 && (
          <div style={{ marginBottom: '24px' }}>
            <div style={{ fontSize: '13px', fontWeight: '700', letterSpacing: '2px', textTransform: 'uppercase', color: '#1a2a3a', marginBottom: '12px', fontFamily: "'Inter', sans-serif" }}>Experience</div>
            {resume.experience.map((exp, i) => (
              <div key={i} style={{ marginBottom: '14px' }}>
                <div style={{ fontSize: '12px', color: '#666', marginBottom: '3px' }}>{exp.duration}</div>
                <div style={{ fontSize: '13px', fontWeight: '600', color: '#1a2a3a', marginBottom: '4px' }}>{exp.title}{exp.company && ` | ${exp.company}`}</div>
                {exp.bullets?.map((b, j) => <div key={j} style={{ fontSize: '12px', color: '#444', lineHeight: '1.7' }}>{b}</div>)}
              </div>
            ))}
            <div style={{ height: '1px', background: '#ccc', marginTop: '16px' }} />
          </div>
        )}
      </div>
    </div>
  );
}

// ─── TEMPLATE 12: CREAM 3-COLUMN (Kristi Larr Beige Style) ───
function Template12({ resume, photo }) {
  return (
    <div style={{ fontFamily: "'Inter', sans-serif", background: '#faf8f0', minHeight: '1123px', width: '794px', padding: '48px 44px', boxSizing: 'border-box' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
        <div>
          {resume.name && (
            <>
              <div style={{ fontSize: '40px', fontWeight: '700', color: '#8B2500', letterSpacing: '4px', textTransform: 'uppercase', lineHeight: '1.1' }}>
                {resume.name.split(' ')[0]}
              </div>
              <div style={{ fontSize: '40px', fontWeight: '400', color: '#8B2500', letterSpacing: '4px', textTransform: 'uppercase', lineHeight: '1.1', marginBottom: '8px' }}>
                {resume.name.split(' ').slice(1).join(' ')}
              </div>
            </>
          )}
          <div style={{ fontSize: '13px', letterSpacing: '3px', textTransform: 'uppercase', color: '#8B2500', fontWeight: '400' }}>
            {resume.experience?.[0]?.title || ''}
          </div>
        </div>
        {photo && <img src={photo} alt="profile" style={{ width: '100px', height: '100px', borderRadius: '50%', objectFit: 'cover', border: '2px solid #d4956a' }} />}
      </div>

      <div style={{ height: '2px', background: '#c0633a', margin: '16px 0' }} />

      {/* Contact Row */}
      <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr 1fr', gap: '16px', marginBottom: '8px' }}>
        <div>
          <div style={{ fontSize: '12px', fontWeight: '700', color: '#8B2500', letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '8px' }}>Contact Info</div>
        </div>
        <div style={{ fontSize: '12px', color: '#333', lineHeight: '1.7' }}>
          {resume.location && <div>{resume.location}</div>}
          {resume.phone && <div>{resume.phone}</div>}
        </div>
        <div style={{ fontSize: '12px', color: '#333', lineHeight: '1.7' }}>
          {resume.email && <div>{resume.email}</div>}
          {resume.linkedin && <div>{resume.linkedin}</div>}
        </div>
      </div>

      <div style={{ height: '2px', background: '#c0633a', margin: '8px 0 20px' }} />

      {/* 3-Column: Education | Summary | Skills */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '24px', marginBottom: '24px' }}>
        {/* Education */}
        <div>
          <div style={{ fontSize: '12px', fontWeight: '700', color: '#8B2500', letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '10px' }}>Education</div>
          {resume.education?.map((edu, i) => (
            <div key={i} style={{ marginBottom: '8px' }}>
              <div style={{ fontSize: '12px', fontWeight: '700', color: '#1a1a1a' }}>{edu.school}</div>
              <div style={{ fontSize: '12px', fontWeight: '700', color: '#1a1a1a' }}>{edu.degree}</div>
              <div style={{ fontSize: '11px', color: '#555', lineHeight: '1.6' }}>{edu.year}</div>
            </div>
          ))}
        </div>

        {/* Summary */}
        <div>
          <div style={{ fontSize: '12px', fontWeight: '700', color: '#8B2500', letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '10px' }}>Summary</div>
          {resume.summary && <p style={{ fontSize: '12px', color: '#444', lineHeight: '1.7', margin: 0 }}>{resume.summary}</p>}
        </div>

        {/* Skills */}
        <div>
          <div style={{ fontSize: '12px', fontWeight: '700', color: '#8B2500', letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '10px' }}>Skills</div>
          {resume.skills?.map((s, i) => <div key={i} style={{ fontSize: '12px', color: '#444', lineHeight: '1.7' }}>{s}</div>)}
        </div>
      </div>

      <div style={{ height: '2px', background: '#c0633a', margin: '0 0 20px' }} />

      {/* Experience */}
      {resume.experience?.length > 0 && (
        <div>
          <div style={{ fontSize: '12px', fontWeight: '700', color: '#8B2500', letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '14px' }}>Experience</div>
          {resume.experience.map((exp, i) => (
            <div key={i} style={{ marginBottom: '16px' }}>
              <div style={{ fontSize: '13px', fontWeight: '700', color: '#1a1a1a', marginBottom: '2px' }}>{exp.duration}</div>
              <div style={{ fontSize: '13px', color: '#333', marginBottom: '4px' }}>{exp.title}{exp.company && ` | ${exp.company}`}</div>
              {exp.bullets?.map((b, j) => <div key={j} style={{ fontSize: '12px', color: '#555', lineHeight: '1.7' }}>{b}</div>)}
            </div>
          ))}
        </div>
      )}

      <div style={{ height: '2px', background: '#c0633a', marginTop: '16px' }} />
    </div>
  );
}

// ─── TEMPLATE 13: PINK/MAUVE TWO COLUMN (Shir Rosenstein Style) ───
function Template13({ resume, photo }) {
  return (
    <div style={{ fontFamily: "'Inter', sans-serif", background: '#f5ede8', minHeight: '1123px', width: '794px', boxSizing: 'border-box' }}>
      {/* Header */}
      <div style={{ background: '#f5ede8', padding: '40px 40px 20px', borderBottom: '4px solid #8B6B6B' }}>
        {resume.name && <h1 style={{ fontSize: '32px', fontWeight: '800', color: '#5C3D3D', margin: '0 0 4px', letterSpacing: '1px' }}>{resume.name}</h1>}
        {resume.experience?.[0]?.title && <div style={{ fontSize: '14px', color: '#8B6B6B', letterSpacing: '2px', textTransform: 'uppercase', fontWeight: '400' }}>{resume.experience[0].title}</div>}
        {photo && <img src={photo} alt="profile" style={{ position: 'absolute', top: '32px', right: '40px', width: '80px', height: '80px', borderRadius: '4px', objectFit: 'cover' }} />}
      </div>

      <div style={{ height: '6px', background: '#8B6B6B' }} />

      {/* Body - 2 columns */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 240px', gap: '0', minHeight: '900px' }}>
        {/* Left - Main Content */}
        <div style={{ padding: '28px 32px', borderRight: '1px solid #d4b8b0' }}>
          {/* Education */}
          {resume.education?.length > 0 && (
            <div style={{ marginBottom: '24px' }}>
              <div style={{ fontSize: '13px', fontWeight: '700', letterSpacing: '2px', textTransform: 'uppercase', color: '#5C3D3D', marginBottom: '12px', borderBottom: '1px solid #d4b8b0', paddingBottom: '6px' }}>Education</div>
              {resume.education.map((edu, i) => (
                <div key={i} style={{ marginBottom: '12px' }}>
                  <div style={{ fontSize: '13px', fontWeight: '700', color: '#1a1a1a' }}>{edu.school}</div>
                  <div style={{ fontSize: '12px', color: '#555' }}>{edu.degree} {edu.year && `| ${edu.year}`}</div>
                </div>
              ))}
            </div>
          )}

          {/* Experience */}
          {resume.experience?.length > 0 && (
            <div>
              <div style={{ fontSize: '13px', fontWeight: '700', letterSpacing: '2px', textTransform: 'uppercase', color: '#5C3D3D', marginBottom: '12px', borderBottom: '1px solid #d4b8b0', paddingBottom: '6px' }}>Professional Experience</div>
              {resume.experience.map((exp, i) => (
                <div key={i} style={{ marginBottom: '16px' }}>
                  <div style={{ fontSize: '13px', fontWeight: '700', color: '#1a1a1a' }}>{exp.title}{exp.company && ` | ${exp.company}`}</div>
                  <div style={{ fontSize: '12px', color: '#8B6B6B', marginBottom: '4px' }}>{exp.duration}</div>
                  {exp.bullets?.map((b, j) => <div key={j} style={{ fontSize: '12px', color: '#555', lineHeight: '1.7', marginBottom: '2px' }}>{b}</div>)}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right Sidebar */}
        <div style={{ padding: '28px 24px', background: '#ede0d8' }}>
          {/* Contact */}
          <div style={{ marginBottom: '20px' }}>
            <div style={{ fontSize: '12px', fontWeight: '700', letterSpacing: '2px', textTransform: 'uppercase', color: '#5C3D3D', marginBottom: '10px', borderBottom: '1px solid #c4a89a', paddingBottom: '4px' }}>Contact Info</div>
            {resume.location && <div style={{ fontSize: '11px', color: '#444', lineHeight: '1.7' }}>{resume.location}</div>}
            {resume.phone && <div style={{ fontSize: '11px', color: '#444', lineHeight: '1.7' }}>{resume.phone}</div>}
            {resume.email && <div style={{ fontSize: '11px', color: '#444', lineHeight: '1.7' }}>{resume.email}</div>}
            {resume.linkedin && <div style={{ fontSize: '11px', color: '#444', lineHeight: '1.7' }}>{resume.linkedin}</div>}
          </div>

          {/* Skills */}
          {resume.skills?.length > 0 && (
            <div style={{ marginBottom: '20px' }}>
              <div style={{ fontSize: '12px', fontWeight: '700', letterSpacing: '2px', textTransform: 'uppercase', color: '#5C3D3D', marginBottom: '10px', borderBottom: '1px solid #c4a89a', paddingBottom: '4px' }}>Skills & Abilities</div>
              {resume.skills.map((s, i) => <div key={i} style={{ fontSize: '11px', color: '#444', lineHeight: '1.8' }}>{s}</div>)}
            </div>
          )}

          {/* Summary as Awards */}
          {resume.summary && (
            <div>
              <div style={{ fontSize: '12px', fontWeight: '700', letterSpacing: '2px', textTransform: 'uppercase', color: '#5C3D3D', marginBottom: '10px', borderBottom: '1px solid #c4a89a', paddingBottom: '4px' }}>Summary</div>
              <p style={{ fontSize: '11px', color: '#444', lineHeight: '1.8', margin: 0 }}>{resume.summary}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── MAIN COMPONENT ───
export default function ResumePreview({ resume, score, templateId }) {
  // templateId comes from the picker screen in Builder.jsx — this is now the single source of truth.
  // Falls back to 1 (Classic) only if no template was ever picked, so the preview never crashes.
  const activeTemplate = templateId || 1;
  const [photo, setPhoto] = useState(null);
  const fileRef = useRef(null);
  const wrapRef = useRef(null);
  const paperRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const update = () => {
      const available = el.clientWidth - 24; // small side margin
      const next = available > 0 ? Math.min(1, available / PAPER_WIDTH) : 1;
      setScale(next);
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => setPhoto(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleDownload = async () => {
    if (!paperRef.current || downloading) return;
    setDownloading(true);
    const prevScale = scale;
    try {
      // Force full resolution (scale 1) for a crisp capture, then restore
      setScale(1);
      await new Promise((r) => setTimeout(r, 150)); // let the re-render settle

      const html2canvas = (await import('html2canvas')).default;
      const { jsPDF } = await import('jspdf');

      const canvas = await html2canvas(paperRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        width: PAPER_WIDTH,
        windowWidth: PAPER_WIDTH,
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'px',
        format: [PAPER_WIDTH, canvas.height * (PAPER_WIDTH / canvas.width)],
      });
      const pdfHeight = canvas.height * (PAPER_WIDTH / canvas.width);
      pdf.addImage(imgData, 'PNG', 0, 0, PAPER_WIDTH, pdfHeight);
      pdf.save(`${(resume.name || 'resume').replace(/\s+/g, '_')}_resume.pdf`);
    } catch (err) {
      alert('Failed to generate PDF: ' + err.message);
    } finally {
      setScale(prevScale);
      setDownloading(false);
    }
  };

  const isEmpty = !resume?.name && !resume?.summary && !resume?.experience?.length;

  const renderTemplate = () => {
    const props = { resume, photo };
    switch (activeTemplate) {
      case 1: return <ClassicTemplate {...props} />;
      case 2: return <ModernTemplate {...props} />;
      case 3: return <MinimalTemplate {...props} />;
      case 4: return <BoldTemplate {...props} />;
      case 5: return <ProfessionalTemplate {...props} />;
      case 6: return <ExecutiveTemplate {...props} />;
      case 7: return <CreativeTemplate {...props} />;
      case 8: return <TechTemplate {...props} />;
      case 9: return <ElegantTemplate {...props} />;
      case 10: return <FreshTemplate {...props} />;
      case 11: return <Template11 {...props} />;
      case 12: return <Template12 {...props} />;
      case 13: return <Template13 {...props} />;
      default: return <ClassicTemplate {...props} />;
    }
  };

  return (
    <div ref={wrapRef} style={styles.container}>
      <style>{`
        @media (max-width: 420px) {
          .rp-header { padding: 12px 14px !important; }
          .rp-header span { font-size: 14px !important; }
          .rp-download-btn { padding: 7px 10px !important; font-size: 12px !important; }
          .rp-photo-row { padding: 8px 14px !important; }
        }
      `}</style>
      {/* Header */}
      <div className="rp-header" style={styles.header}>
        <span style={styles.headerTitle}>Live Preview</span>
        <button className="rp-download-btn" style={{ ...styles.downloadBtn, opacity: downloading ? 0.6 : 1 }} onClick={handleDownload} disabled={isEmpty || downloading}>
          {downloading ? '⏳ Generating...' : '⬇ Download PDF'}
        </button>
      </div>

      {/* Photo Upload */}
      <div className="rp-photo-row" style={styles.photoRow}>
        <input type="file" accept="image/*" ref={fileRef} onChange={handlePhotoUpload} style={{ display: 'none' }} />
        {photo ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <img src={photo} alt="profile" style={{ width: '36px', height: '36px', borderRadius: '50%', objectFit: 'cover', border: '2px solid #a78bfa' }} />
            <span style={styles.photoLabel}>Photo added ✓</span>
            <button style={styles.photoBtn} onClick={() => setPhoto(null)}>Remove</button>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <button style={styles.photoBtn} onClick={() => fileRef.current.click()}>📷 Add Photo</button>
            <span style={styles.photoLabel}>or <span style={{ color: '#a78bfa', cursor: 'pointer' }} onClick={() => {}}>skip</span></span>
          </div>
        )}
      </div>

      {/* Score */}
      {score && score.total > 0 && (
        <div style={styles.scoreCard}>
          <div style={styles.scoreTop}>
            <span style={styles.scoreLabel}>ATS Score</span>
            <span style={styles.scoreValue}>{score.total}/100</span>
          </div>
          <div style={styles.scoreBar}>
            <div style={{ ...styles.scoreBarFill, width: `${score.total}%`, background: score.total >= 75 ? '#4ade80' : score.total >= 50 ? '#facc15' : '#f87171' }} />
          </div>
        </div>
      )}

      {/* Resume Paper - scaled to fit the available width on any device */}
      <div style={{ width: '100%', display: 'flex', justifyContent: 'center', overflow: 'hidden' }}>
        <div
          style={{
            width: PAPER_WIDTH * scale,
            height: PAPER_MIN_HEIGHT * scale,
            margin: '16px 0',
            flexShrink: 0,
          }}
        >
          <div
            ref={paperRef}
            style={{
              ...styles.paper,
              transform: `scale(${scale})`,
              transformOrigin: 'top left',
              margin: 0,
            }}
          >
            {isEmpty ? (
              <div style={styles.empty}>
                <p style={styles.emptyIcon}>📄</p>
                <p style={styles.emptyText}>Your resume will appear here as you chat</p>
              </div>
            ) : renderTemplate()}
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: { display: 'flex', flexDirection: 'column', minHeight: '100%', background: '#141414', overflowY: 'auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 20px', borderBottom: '1px solid #222' },
  headerTitle: { color: '#f0f0f0', fontWeight: '600', fontSize: '15px', fontFamily: "'Inter', sans-serif" },
  downloadBtn: { background: '#a78bfa', color: '#fff', border: 'none', borderRadius: '8px', padding: '8px 14px', fontSize: '13px', cursor: 'pointer', fontFamily: "'Inter', sans-serif" },
  photoRow: { padding: '10px 20px', borderBottom: '1px solid #1a1a1a', display: 'flex', alignItems: 'center' },
  photoBtn: { fontSize: '12px', padding: '5px 12px', borderRadius: '6px', border: '1px solid #333', background: '#1a1a1a', color: '#ccc', cursor: 'pointer' },
  photoLabel: { fontSize: '12px', color: '#666', fontFamily: "'Inter', sans-serif" },
  templateRow: { display: 'flex', gap: '6px', padding: '10px 20px', borderBottom: '1px solid #1a1a1a', flexWrap: 'wrap' },
  templateBtn: { fontSize: '11px', padding: '5px 12px', borderRadius: '20px', border: '1px solid', cursor: 'pointer', fontFamily: "'Inter', sans-serif", transition: 'all 0.2s' },
  scoreCard: { margin: '12px 20px 0', background: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: '10px', padding: '12px 14px' },
  scoreTop: { display: 'flex', justifyContent: 'space-between', marginBottom: '6px' },
  scoreLabel: { color: '#888', fontSize: '12px', fontFamily: "'Inter', sans-serif" },
  scoreValue: { color: '#f0f0f0', fontWeight: '700', fontSize: '14px', fontFamily: "'Inter', sans-serif" },
  scoreBar: { height: '6px', background: '#2a2a2a', borderRadius: '4px', overflow: 'hidden' },
  scoreBarFill: { height: '100%', borderRadius: '4px', transition: 'width 0.4s ease' },
  paper: { background: '#fff', borderRadius: '4px', overflow: 'hidden', boxShadow: '0 4px 32px rgba(0,0,0,0.5)', width: PAPER_WIDTH, minHeight: PAPER_MIN_HEIGHT },
  empty: { textAlign: 'center', paddingTop: '60px' },
  emptyIcon: { fontSize: '40px', marginBottom: '12px' },
  emptyText: { color: '#aaa', fontSize: '14px', fontFamily: "'Inter', sans-serif" },
};
